import { useEffect, useState } from 'react';

import { Button } from '@components/button';
import { CodeBlock } from '@components/code-block';
import { useDebouncedValue } from '@components/component-helpers';
import { createForm, useCreateFormStore } from '@components/form';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconCloseRegular } from '@components/icons/close-regular';
import { InlineSidebar } from '@components/inline-sidebar';
import { Spinner } from '@components/loading-spinner';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextArea } from '@components/textarea';
import { useEvent } from '@hooks/use-event';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { useFieldTypes } from '../common';
import { useValidateJqQuery, useGenerateJqQuery } from '../integrations-hooks';
import { IntegrationEndpointVariable } from '../integrations-types';

// Custom response values are IntegrationEndpointVariables with a jqQuery
type CustomResponseValue = Pick<IntegrationEndpointVariable, 'name' | 'label' | 'type'> & {
  jqQuery?: string;
};

type Props = {
  value: CustomResponseValue | null;
  exampleData: object | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (value: IntegrationEndpointVariable) => void;
  dataTitle?: string;
  dataDescription?: string;
};

type FormData = {
  label: string;
  type: string;
  jqQuery: string;
};

const Form = createForm<FormData>();

export const EditCustomResponseValueSidebar = ({
  value,
  exampleData,
  isOpen,
  onClose,
  onSave,
  dataTitle,
  dataDescription,
}: Props) => {
  const [aiPrompt, setAiPrompt] = useState('');
  const isAdding = !value?.jqQuery && !value?.label;

  const typeOptions = useFieldTypes();
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();
  const validateMutation = useValidateJqQuery();
  const generateMutation = useGenerateJqQuery();
  const jqQuery = formStore.useFieldValue<string>('jqQuery');
  const debouncedJqQuery = useDebouncedValue(jqQuery, 500);

  const validate = useEvent((query) => {
    if (!debouncedJqQuery || !exampleData) {
      return;
    }
    validateMutation.mutate({ query, exampleData });
  });

  useEffect(() => {
    validate(debouncedJqQuery);
  }, [debouncedJqQuery, validate]);

  const handleGenerateQuery = () => {
    if (!aiPrompt || !exampleData) {
      return;
    }

    generateMutation.mutate(
      { prompt: aiPrompt, exampleData },
      {
        onSuccess: (result) => {
          if (result.success && result.query) {
            formStore.actions.setValue('jqQuery', result.query);
            if (result.suggestedType) {
              formStore.actions.setValue('type', result.suggestedType);
            }
          }
        },
      }
    );
  };

  const isValidationPending = jqQuery !== debouncedJqQuery || validateMutation.isLoading;
  const hasValidationError = Boolean(validateMutation.data?.error);
  const queryResult = validateMutation.data?.result;
  // A custom value is stored as a single value; an object or list would be
  // split into separate keys when the payload is flattened, so block it here.
  const resultIsObjectOrList = typeof queryResult === 'object' && queryResult !== null;
  const isSaveDisabled =
    !isDirty ||
    isValidationPending ||
    hasValidationError ||
    resultIsObjectOrList ||
    generateMutation.isLoading;

  const handleSubmit = async (formValues: FormData) => {
    if (!value) return;

    const savedValue = {
      name: value.name,
      label: formValues.label || value.label,
      type: formValues.type || value.type || 'text',
      jqQuery: formValues.jqQuery || value.jqQuery,
    } as IntegrationEndpointVariable;

    onSave(savedValue);
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  if (!value) return null;

  return (
    <Form store={formStore} onSubmit={handleSubmit}>
      <InlineSidebar
        isOpen={isOpen}
        onOpenChange={handleClose}
        ariaLabel={t('editCustomResponseValue')}
      >
        <InlineSidebar.ExpandedPanel isExpanded>
          {generateMutation.isLoading && <Spinner />}
          <InlineSidebar.Header>
            <Stack orientation="horizontal" align="center" justify="space-between">
              <Text font="bold18" text={dataTitle ?? t('endpointData')} />
            </Stack>
          </InlineSidebar.Header>

          <InlineSidebar.Content>
            {exampleData ? (
              <CodeBlock
                data={exampleData}
                hideFooter
                // Account for sidebar padding (80px) and header height (44px)
                maxHeight={`calc(100vh - ${pxToRem(124)})`}
              />
            ) : (
              <Stack space={1}>
                <Text text={t('noEndpointData')} font="semibold14" color="orange100" />
                <Text text={t('testEndpointFirst')} font="regular14" color="textSecondary" />
              </Stack>
            )}
          </InlineSidebar.Content>
        </InlineSidebar.ExpandedPanel>

        <InlineSidebar.MainPanel>
          <InlineSidebar.Header>
            <Stack orientation="horizontal" align="center" justify="space-between">
              <Text
                font="bold18"
                text={isAdding ? t('addCustomResponseValue') : t('editCustomResponseValue')}
              />
              <IconButton
                icon={<IconCloseRegular />}
                variant="ghost-purple"
                onClick={handleClose}
              />
            </Stack>
          </InlineSidebar.Header>

          <InlineSidebar.Content>
            <Stack space={4}>
              <Form.TextField
                id="label"
                label={t('label')}
                defaultValue={value.label}
                placeholder="Label"
                validators={['required']}
                isDisabled={validateMutation.isLoading || generateMutation.isLoading}
              />

              <Form.Select
                id="type"
                label={t('type')}
                defaultValue={value.type || 'text'}
                options={typeOptions}
                validators={['required']}
                isDisabled={validateMutation.isLoading || generateMutation.isLoading}
              />

              {exampleData && (
                <Stack space={2}>
                  <Text text={t('aiPrompt')} font="semibold14" />
                  <TextArea
                    rows={2}
                    onChange={setAiPrompt}
                    placeholder={t('aiPromptPlaceholder')}
                    isDisabled={validateMutation.isLoading || generateMutation.isLoading}
                  />
                  <div>
                    <Button
                      text={generateMutation.isLoading ? t('generating') : t('generateJqQuery')}
                      variant="secondary"
                      onClick={handleGenerateQuery}
                      isDisabled={
                        !aiPrompt || generateMutation.isLoading || validateMutation.isLoading
                      }
                      iconLeft={<IconAddPlus />}
                    />
                  </div>
                </Stack>
              )}

              <Form.Textarea
                id="jqQuery"
                label={t('jqQuery')}
                defaultValue={value.jqQuery}
                placeholder=".data[0].id"
                validators={['required']}
                isDisabled={generateMutation.isLoading}
              />

              <Text
                text={dataDescription ?? t('queryInfo')}
                font="regular14"
                color="textSecondary"
              />

              {validateMutation.isLoading && (
                <Text text={t('validating')} font="regular14" color="textSecondary" />
              )}

              {validateMutation.data?.success && validateMutation.data.result !== null && (
                <Stack space={2}>
                  <Text
                    text={t('queryResult')}
                    font="semibold14"
                    color={resultIsObjectOrList ? 'red100' : 'elementGreen100'}
                  />
                  <CodeBlock data={validateMutation.data.result} hideFooter />
                  {resultIsObjectOrList && (
                    <Text text={t('objectOrListResult')} font="regular14" color="textSecondary" />
                  )}
                </Stack>
              )}

              {validateMutation.data?.error && !validateMutation.isLoading && (
                <Stack space={1}>
                  <Text text={t('queryError')} font="semibold14" color="red100" />
                  <Text text={validateMutation.data.error} font="regular14" color="textSecondary" />
                </Stack>
              )}

              {generateMutation.data?.error && !generateMutation.isLoading && (
                <Stack space={1}>
                  <Text text={t('queryError')} font="semibold14" color="red100" />
                  <Text text={generateMutation.data.error} font="regular14" color="textSecondary" />
                </Stack>
              )}
            </Stack>
          </InlineSidebar.Content>

          <InlineSidebar.Footer>
            <Stack orientation="horizontal" space={2} justify="flex-end">
              <Button text={t('core.cancel')} variant="secondary" onClick={handleClose} />
              <Form.Submit
                text={t('core.save')}
                variant="primary-blue"
                isDisabled={isSaveDisabled}
              />
            </Stack>
          </InlineSidebar.Footer>
        </InlineSidebar.MainPanel>
      </InlineSidebar>
    </Form>
  );
};

const locale = {
  en: {
    addCustomResponseValue: 'Add Custom Response Value',
    editCustomResponseValue: 'Edit Custom Response Value',
    label: 'Label',
    type: 'Type',
    jqQuery: './jq Query',
    endpointData: 'Endpoint Data',
    noEndpointData: 'No endpoint data available',
    testEndpointFirst:
      'Test the endpoint first to generate example data. The jq query will run against the test response.',
    queryInfo:
      'The jq query will be executed against the API response when the integration runs. Refer to https://jqlang.github.io/jq/ for syntax.',
    validating: 'Validating query...',
    queryResult: 'Query Result',
    objectOrListResult:
      'The query must return a single value, not an object or list. Select a field.',
    queryError: 'Query Error',
    aiPrompt: 'AI Prompt',
    aiPromptPlaceholder: 'Describe what you want to select and have AI structure it for you',
    generateJqQuery: 'Generate jq query',
    generating: 'Generating...',
    showEndpointData: 'Show endpoint data',
    hideEndpointData: 'Hide endpoint data',
  },
};
